import { DASHBOARD_COLUMN_COUNT, GridLayoutItem } from "./DashboardGrid";
import { DashboardLayout, DashboardWidgetLayout } from "./dashboardLayout";

export function getDashboardLayoutFromGrid<WidgetType extends string>(
  gridLayout: ReadonlyArray<GridLayoutItem>,
  currentLayout: DashboardLayout<WidgetType>,
): DashboardLayout<WidgetType> {
  const widgetsById = new Map(
    currentLayout.widgets.map((widget) => [widget.id, widget]),
  );

  return {
    ...currentLayout,
    widgets: gridLayout
      .filter((item) => widgetsById.has(item.i))
      .map((item) => {
        const widget = widgetsById.get(item.i)!;
        return {
          ...widget,
          height: item.h,
          width: item.w,
          x: item.x,
          y: item.y,
        };
      }),
  };
}

function getOverlapArea<WidgetType extends string>(
  first: Pick<
    DashboardWidgetLayout<WidgetType>,
    "height" | "width" | "x" | "y"
  >,
  second: Pick<GridLayoutItem, "h" | "w" | "x" | "y">,
) {
  const overlapWidth = Math.max(
    0,
    Math.min(first.x + first.width, second.x + second.w) -
      Math.max(first.x, second.x),
  );
  const overlapHeight = Math.max(
    0,
    Math.min(first.y + first.height, second.y + second.h) -
      Math.max(first.y, second.y),
  );
  return overlapWidth * overlapHeight;
}

function widgetsOverlap<WidgetType extends string>(
  first: DashboardWidgetLayout<WidgetType>,
  second: DashboardWidgetLayout<WidgetType>,
) {
  return (
    first.x < second.x + second.width &&
    first.x + first.width > second.x &&
    first.y < second.y + second.height &&
    first.y + first.height > second.y
  );
}

function getHorizontalWidgetSwapTarget<WidgetType extends string>(
  layout: DashboardLayout<WidgetType>,
  droppedItem: GridLayoutItem,
) {
  const draggedWidget = layout.widgets.find(
    (widget) => widget.id === droppedItem.i,
  );
  if (!draggedWidget) {
    return undefined;
  }

  return layout.widgets
    .filter(
      (widget) =>
        widget.id !== draggedWidget.id && widget.y === draggedWidget.y,
    )
    .map((widget) => ({
      overlapArea: getOverlapArea(widget, droppedItem),
      widget,
    }))
    .filter(({ overlapArea }) => overlapArea > 0)
    .sort((first, second) => second.overlapArea - first.overlapArea)[0]?.widget;
}

export function getHorizontalWidgetSwap<WidgetType extends string>(
  layout: DashboardLayout<WidgetType>,
  droppedItem: GridLayoutItem,
): DashboardLayout<WidgetType> | null {
  const draggedWidget = layout.widgets.find(
    (widget) => widget.id === droppedItem.i,
  );
  if (!draggedWidget) {
    return null;
  }

  const targetWidget = getHorizontalWidgetSwapTarget(layout, droppedItem);
  if (!targetWidget) {
    return null;
  }

  const draggedMovesRight = draggedWidget.x < targetWidget.x;
  const nextDraggedWidget = {
    ...draggedWidget,
    x: draggedMovesRight
      ? targetWidget.x + targetWidget.width - draggedWidget.width
      : targetWidget.x,
  };
  const nextTargetWidget = {
    ...targetWidget,
    x: draggedMovesRight
      ? draggedWidget.x
      : draggedWidget.x + draggedWidget.width - targetWidget.width,
  };
  const swappedWidgets = [nextDraggedWidget, nextTargetWidget];
  const otherWidgets = layout.widgets.filter(
    (widget) => widget.id !== draggedWidget.id && widget.id !== targetWidget.id,
  );
  const isValidSwap =
    swappedWidgets.every(
      (widget) =>
        widget.x >= 0 && widget.x + widget.width <= DASHBOARD_COLUMN_COUNT,
    ) &&
    !widgetsOverlap(nextDraggedWidget, nextTargetWidget) &&
    swappedWidgets.every((widget) =>
      otherWidgets.every((otherWidget) => !widgetsOverlap(widget, otherWidget)),
    );
  if (!isValidSwap) {
    return null;
  }

  return {
    ...layout,
    widgets: layout.widgets.map((widget) => {
      if (widget.id === nextDraggedWidget.id) {
        return nextDraggedWidget;
      }
      if (widget.id === nextTargetWidget.id) {
        return nextTargetWidget;
      }
      return widget;
    }),
  };
}

export function applyDashboardPositionsToGrid<WidgetType extends string>(
  gridLayout: ReadonlyArray<GridLayoutItem>,
  dashboardLayout: DashboardLayout<WidgetType>,
) {
  const widgetsById = new Map(
    dashboardLayout.widgets.map((widget) => [widget.id, widget]),
  );
  gridLayout.forEach((item) => {
    const widget = widgetsById.get(item.i);
    if (widget) {
      item.x = widget.x;
      item.y = widget.y;
    }
  });
}
